// Single source of truth for auth token storage + the silent-refresh flow.
//
// Previously there were three independent fetch wrappers (lib/api.ts's
// ApiClient, api/walletIntel.ts's req(), api/profiler.ts's req()), each with
// its own copy of "on 401, clear the token and hard-redirect to /login" —
// and nothing anywhere renewed a token before it expired. With access tokens
// living only 30 minutes and no refresh path wired up, any session running
// longer than that (a wallet trace, a batch screen, a report export) hit a
// full-page redirect mid-task with zero warning. This module gives all three
// call sites one shared refresh path instead: try to silently exchange the
// refresh token for a new access token before giving up and logging out.

const ACCESS_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const IMPERSONATION_INFO_KEY = 'impersonation_info';
const ADMIN_BACKUP_ACCESS_KEY = 'admin_backup_access_token';
const ADMIN_BACKUP_REFRESH_KEY = 'admin_backup_refresh_token';

// typeof window guards below make these safe to call at module-eval time
// (ApiClient's constructor does exactly that, as a module-level singleton)
// under Node/SSR (scripts/prerender.mjs), where localStorage doesn't exist.
// No behavior change in the browser — window is always defined there.

export function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setTokens(accessToken: string, refreshToken?: string | null): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/** Decode a JWT's payload without a library — just base64url + JSON.parse.
 * Never throws; returns null for anything malformed. */
export function decodeJwtPayload(token: string): { exp?: number; sub?: string; user_id?: string } | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(b64)
        .split('')
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** Milliseconds until this access token expires, or null if undecodable. */
export function msUntilExpiry(token: string): number | null {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return null;
  return payload.exp * 1000 - Date.now();
}

// De-duplicates concurrent refresh attempts — if five requests all 401 at
// once, they should trigger exactly one POST /auth/refresh, not five.
let refreshInFlight: Promise<string | null> | null = null;

/** Exchange the stored refresh token for a new access token. Returns the new
 * access token on success, or null if there's no refresh token, it's
 * expired/invalid, or the request failed — callers should treat null as
 * "give up and log out for real." Safe to call concurrently. */
export async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  refreshInFlight = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) {
        clearTokens();
        return null;
      }
      const data = await res.json();
      if (!data.access_token) {
        clearTokens();
        return null;
      }
      setTokens(data.access_token);
      return data.access_token as string;
    } catch {
      // Network error — don't clear tokens, the user may just be briefly
      // offline; the next attempt (or the reactive 401 path) will retry.
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/** Log the user out everywhere this tab knows about — clears storage and
 * hard-redirects to /login. This is the ONLY place that should do the
 * hard redirect; every 401 handler should try refreshAccessToken() first. */
export function forceLogout(): void {
  clearTokens();
  window.location.href = '/login';
}

// --- Impersonation (Admin Panel Redesign) -----------------------------------
// POST /admin/users/{id}/impersonate issues a short-lived (15 min),
// non-refreshable access token for the target user. These helpers swap it
// into the same storage slot every apiClient request already reads
// (getAccessToken() above is re-read fresh on every call, so nothing else
// needs to change for requests to start acting as the impersonated user),
// while backing up the admin's own session so "Exit" restores it instantly.
// Single-tab only: a second tab open during impersonation sees the swapped
// token but not the banner/state — not hardened further for this pass.

export interface ImpersonationInfo {
  targetEmail: string;
  targetUserId: string;
  adminEmail: string;
  expiresAt: number; // ms epoch
}

/** Fired by the fetch wrappers (lib/api.ts, lib/apiRequest.ts) when a 401
 * survives a silent-refresh attempt WHILE impersonating, instead of the
 * usual forceLogout(). AuthContext listens for this and restores the
 * admin's own session — see the bug this fixes: startImpersonation()
 * deliberately clears refresh_token for the duration (non-refreshable by
 * design), so without this a stray 401 for *any* reason during those 15
 * minutes — or just the token's natural expiry — used to hard-redirect to
 * /login and torch the whole session instead of gracefully falling back to
 * the admin, making "Exit" look broken (the app was already gone before it
 * could be clicked, or exiting = a full logout instead of a clean restore). */
export const IMPERSONATION_ENDED_EVENT = 'sycek:impersonation-ended';

export function isImpersonating(): boolean {
  return getImpersonationInfo() !== null;
}

export function getImpersonationInfo(): ImpersonationInfo | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(IMPERSONATION_INFO_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function startImpersonation(
  accessToken: string,
  expiresInSeconds: number,
  info: Omit<ImpersonationInfo, 'expiresAt'>
): void {
  const currentAccess = getAccessToken();
  const currentRefresh = getRefreshToken();
  if (currentAccess) localStorage.setItem(ADMIN_BACKUP_ACCESS_KEY, currentAccess);
  if (currentRefresh) localStorage.setItem(ADMIN_BACKUP_REFRESH_KEY, currentRefresh);

  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  // Deliberately cleared, not just left stale: the impersonation token is
  // non-refreshable, so a stale admin refresh_token sitting here would let a
  // background silent-refresh silently swap the admin back in while the
  // "Viewing as" banner is still showing.
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.setItem(
    IMPERSONATION_INFO_KEY,
    JSON.stringify({ ...info, expiresAt: Date.now() + expiresInSeconds * 1000 })
  );
}

/** Restore the admin's own session after "Exit" (or automatically once the
 * impersonation token expires). Returns false — a safe no-op — if there's
 * nothing to restore, e.g. this was already called. */
export function exitImpersonation(): boolean {
  const backupAccess = localStorage.getItem(ADMIN_BACKUP_ACCESS_KEY);
  if (!backupAccess) {
    localStorage.removeItem(IMPERSONATION_INFO_KEY);
    return false;
  }
  const backupRefresh = localStorage.getItem(ADMIN_BACKUP_REFRESH_KEY);
  localStorage.setItem(ACCESS_TOKEN_KEY, backupAccess);
  if (backupRefresh) localStorage.setItem(REFRESH_TOKEN_KEY, backupRefresh);
  localStorage.removeItem(ADMIN_BACKUP_ACCESS_KEY);
  localStorage.removeItem(ADMIN_BACKUP_REFRESH_KEY);
  localStorage.removeItem(IMPERSONATION_INFO_KEY);
  return true;
}
